/**
 * 联机 WebSocket Transport（§11.7 / §5.2 / §7.5 / §7.9 / §7.10）。
 *
 * 职责边界：**只管一条消息流的生老病死**——建连、hello、心跳、重连、outbox 重发、
 * 帧序号交给 `applyMessage`。它不认识牌桌、不认识座位，也不做任何渲染判断。
 *
 * 这里有六个「写错就是线上事故」的点，逐条标了 ★：
 *  1. `cid` 跨重连**不重置**（§5.2）：服务端用 `lastProcessedCid` 做双水位去重，
 *     重置就会让重连后的第一条动作被当成很久以前那条的重放而被吞掉。
 *  2. outbox 只装**变更类**消息（`NEVER_DROP_MESSAGE_TYPES`）：查询类重发毫无意义，
 *     ping/pong 重发更是纯浪费。重连后只补发 `cid > welcome.lastProcessedCid` 的。
 *  3. `requestSnapshotOnce` **单飞 + 2s 节流**（红线 #49）。丢一帧之后每条帧都「不连续」，
 *     不节流就会被 20 msg/s 限流 close(4429)，退避重连再来一轮——弱网必雪崩。
 *  4. 判死只看 `lastServerMessageAt`，**绝不看 `readyState`**（§7.10）。
 *  5. `hardReconnect` 第一步是**解绑旧 socket 的全部处理器**：iOS 上旧 socket 的
 *     onclose 可能几秒后才补触发，不解绑就会把刚建好的新连接一起打掉。
 *  6. 重连**不清空牌桌状态**（§7.10），顶部只显示一条「连接中」。
 */
import {
  CLIENT_VERSION,
  PROTOCOL_VERSION,
  WS,
  WS_CLOSE,
  closeBehaviorOf,
  isDroppableOnQueueFull,
  ulid,
  type ClientFrame,
  type ClientMessage,
  type Millis,
  type ResumeRequest,
  type ServerFrame,
  type ServerMessage,
} from '@poker/shared'

import type { ConnectionPhase } from '../store/tableStore.js'
import { useNetStore, type ApplyEffects } from './applyMessage.js'
import { createBackoff, type Backoff, type BackoffResetReason } from './backoff.js'
import { createHeartbeat, realTimers, type Heartbeat, type HeartbeatTimers } from './heartbeat.js'
import { attachLifecycle, type LifecycleEnv, type ReconnectReason } from './lifecycle.js'
import { createServerClock, type ServerClock } from './serverClock.js'

/** 快照请求的节流窗口。与服务端「每连接 1 次 / 2s」的限流对齐（§5.2）。 */
export const SNAPSHOT_THROTTLE_MS = 2_000
/** 单飞看门狗：快照一直不来也不能永远锁死 */
export const SNAPSHOT_INFLIGHT_TIMEOUT_MS = 8_000

// ============================================================================
// 可注入的 WebSocket
// ============================================================================

export interface SocketLike {
  readonly readyState: number
  send(data: string): void
  close(code?: number, reason?: string): void
  onopen: ((ev: unknown) => void) | null
  onclose: ((ev: { code: number; reason: string }) => void) | null
  onerror: ((ev: unknown) => void) | null
  onmessage: ((ev: { data: unknown }) => void) | null
}

export type SocketFactory = (url: string) => SocketLike

const OPEN = 1

function defaultSocketFactory(url: string): SocketLike {
  const Ctor = (globalThis as { WebSocket?: new (u: string) => SocketLike }).WebSocket
  if (Ctor === undefined) throw new Error('当前环境没有 WebSocket')
  return new Ctor(url)
}

// ============================================================================
// 对外接口
// ============================================================================

/**
 * 联机形态的 Transport。形状与 `transport/types.ts` 的 `Transport` 一致，
 * 但消息类型用的是 `@poker/shared` 的**完整协议**（那个文件里的是 M1 本地子集，
 * 两者重名不同构，故不共用；见 deviations）。
 */
export interface WsTransport {
  readonly mode: 'online'
  /** 建连并等到 `welcome`；失败会按退避继续重试，Promise 只反映第一次 */
  connect(): Promise<void>
  send(msg: ClientMessage): number
  /** 返回取消订阅函数 */
  onMessage(cb: (m: ServerMessage, frame: ServerFrame) => void): () => void
  /**
   * 只推送**真正应用了**的消息（被判 duplicate / gap 的帧不推）。
   * 一次性的社交事件（冒气泡、被催震动）必须订这个：`onMessage` 对每一帧都回调，
   * 重复帧会让同一句话冒两次。
   */
  onApplied(cb: (m: ServerMessage, frame: ServerFrame) => void): () => void
  onPhase(cb: (p: ConnectionPhase) => void): () => void
  close(): void
  /** ★ 红线 #49：单飞 + 节流。任何地方想要快照都只能走它 */
  requestSnapshotOnce(reason: 'gap' | 'desync' | 'manual'): void
  /** 用户点「立即重试」 */
  retryNow(): void
  phase(): ConnectionPhase
  readonly clock: ServerClock
  readonly backoff: Backoff
  /** 调试用：当前 outbox 里等待确认的帧数 */
  pendingCount(): number
}

export interface ResumeContext {
  readonly sessionId: string
  readonly resumeToken: string
  readonly roomId: string
  readonly lastServerSeq: number
  readonly lastSv: number
  readonly lastHandNo: number
}

export interface WsTransportOptions {
  readonly clientVersion?: string
  readonly deviceId?: string
  readonly socketFactory?: SocketFactory
  readonly timers?: HeartbeatTimers
  readonly rand?: () => number
  /** null = 不挂生命周期监听（测试/SSR） */
  readonly lifecycleEnv?: LifecycleEnv | null
  /** 收到 4401 时强制刷新令牌；不给就退回 tokenProvider */
  readonly refreshToken?: () => Promise<string>
  /** 默认写进 `useNetStore`；测试里可换成收集器 */
  readonly sink?: (frame: ServerFrame, effects: ApplyEffects) => void
  /** 默认从 `useNetStore` 拼；没有房间就返回 null（首连） */
  readonly resumeContext?: () => ResumeContext | null
  /** 客户端确认到的 seq，随 `pong` 回传给服务端裁剪重发缓冲 */
  readonly ackSeq?: () => number
  readonly onPhaseChange?: (p: ConnectionPhase) => void
}

// ============================================================================
// 实现
// ============================================================================

export function createWsTransport(
  url: string | (() => Promise<string>),
  tokenProvider: () => Promise<string>,
  options: WsTransportOptions = {},
): WsTransport {
  const timers = options.timers ?? realTimers
  const socketFactory = options.socketFactory ?? defaultSocketFactory
  const clientVersion = options.clientVersion ?? CLIENT_VERSION
  const deviceId = options.deviceId ?? `dev_${ulid(Date.now())}`
  const clock = createServerClock({ now: () => Date.now() })
  const backoff = createBackoff(options.rand === undefined ? {} : { rand: options.rand })

  const resolveUrl = typeof url === 'string' ? (): Promise<string> => Promise.resolve(url) : url

  const sink =
    options.sink ??
    ((frame: ServerFrame, effects: ApplyEffects): void => {
      useNetStore.getState().ingestFrame(frame, effects)
    })

  const resumeContext =
    options.resumeContext ??
    ((): ResumeContext | null => {
      const s = useNetStore.getState()
      const session = s.session
      const roomId = s.view?.room.id ?? null
      if (session === null || roomId === null) return null
      return {
        sessionId: session.sessionId,
        resumeToken: session.resumeToken,
        roomId,
        lastServerSeq: s.cursor.appliedSeq,
        lastSv: s.cursor.appliedSv,
        lastHandNo: s.lastHandNo,
      }
    })

  const ackSeq = options.ackSeq ?? ((): number => useNetStore.getState().cursor.appliedSeq)

  // —— 连接状态 ——
  let socket: SocketLike | null = null
  let phase: ConnectionPhase = 'idle'
  let closedByUs = false
  let suspended = false // offline：停止自动重连
  let helloTimer: unknown = null
  let retryTimer: unknown = null
  let connectPromise: Promise<void> | null = null
  let resolveConnect: (() => void) | null = null
  let authRetries = 0
  let backgroundedAt: number | null = null
  let detachLifecycle: (() => void) | null = null

  // —— outbox / cid（★ 跨重连不重置）——
  let cid = 0
  const outbox = new Map<number, ClientFrame>()

  // —— 快照单飞 + 节流 ——
  let snapshotInFlight = false
  let lastSnapshotRequestAt = -Infinity

  const messageSubs = new Set<(m: ServerMessage, f: ServerFrame) => void>()
  const appliedSubs = new Set<(m: ServerMessage, f: ServerFrame) => void>()
  const phaseSubs = new Set<(p: ConnectionPhase) => void>()

  const setPhase = (p: ConnectionPhase): void => {
    if (phase === p) return
    phase = p
    useNetStore.getState().setPhase(p)
    options.onPhaseChange?.(p)
    for (const cb of phaseSubs) cb(p)
  }

  const clearTimer = (h: unknown): void => {
    if (h !== null) timers.clearTimeout(h)
  }

  // ——————————————————————————————————————————————
  // 发送
  // ——————————————————————————————————————————————

  const writeFrame = (frame: ClientFrame): boolean => {
    const s = socket
    if (s === null || s.readyState !== OPEN) return false
    try {
      s.send(JSON.stringify(frame))
      return true
    } catch {
      return false
    }
  }

  /**
   * ★ `hello` / `bye` 虽然在 `NEVER_DROP_MESSAGE_TYPES` 里，但**绝不能进 outbox**：
   *   新连接会自己发一条新的 hello，把旧的那条补发过去等于「一条连接两个 hello」，
   *   网关判定为协议滥用直接 close(4400) —— 重连从此永远失败。
   */
  const outboxable = (type: ClientMessage['type']): boolean =>
    !isDroppableOnQueueFull(type) && type !== 'hello' && type !== 'bye'

  function send(msg: ClientMessage): number {
    cid += 1
    const frame: ClientFrame = { v: PROTOCOL_VERSION, cid, sentAt: Date.now(), msg }
    // 只有变更类进 outbox：查询类重发无意义，ping/pong 重发是纯浪费
    if (outboxable(msg.type)) outbox.set(cid, frame)
    writeFrame(frame)
    return cid
  }

  const heartbeat: Heartbeat = createHeartbeat({
    timers,
    sendPing: (nonce, t0) => {
      send({ type: 'ping', nonce, t0 })
    },
    onDead: () => {
      // §7.9：判死 → close(4000) → **立即**重连，不进退避
      hardReconnect('dead')
    },
  })

  function requestSnapshotOnce(reason: 'gap' | 'desync' | 'manual'): void {
    const now = timers.now()
    if (snapshotInFlight && now - lastSnapshotRequestAt < SNAPSHOT_INFLIGHT_TIMEOUT_MS) return
    if (now - lastSnapshotRequestAt < SNAPSHOT_THROTTLE_MS) return
    // 连接没开时不占用节流额度：requestSnapshot 是可丢类型，发不出去就等于没发，
    // 白占一个 2s 窗口会让重连后的第一次真缺口被推迟
    if (socket === null || socket.readyState !== OPEN) return
    lastSnapshotRequestAt = now
    snapshotInFlight = true
    send({ type: 'requestSnapshot', reason })
  }

  const effects: ApplyEffects = {
    requestSnapshot: requestSnapshotOnce,
  }

  // ——————————————————————————————————————————————
  // 入站
  // ——————————————————————————————————————————————

  function handleFrame(raw: unknown): void {
    if (typeof raw !== 'string') return
    let frame: ServerFrame
    try {
      frame = JSON.parse(raw) as ServerFrame
    } catch {
      return
    }
    if (typeof frame !== 'object' || frame === null || !Array.isArray(frame.msgs)) return

    // ★ 判死基线：任意入站消息都算「活着」，不只是 pong
    heartbeat.noteInbound(timers.now())
    // t3 必须在**收到的这一刻**取，不能等处理完再取：JSON.parse 与 reducer 的耗时
    // 会被整个算进 RTT，慢机器上能虚高几十毫秒
    const t3 = Date.now()

    // ack 是 piggyback 的低水位：≤ 它的帧服务端已经提交，outbox 可以扔了
    if (typeof frame.ack === 'number') trimOutbox(frame.ack)

    let snapped = false
    const pongs: Extract<ServerMessage, { type: 'pong' }>[] = []
    for (const m of frame.msgs) {
      if (m.type === 'pong') {
        snapped = clock.addSample(m.t0, m.t1, m.t2, t3).snapped || snapped
        pongs.push(m)
        continue
      }
      if (m.type === 'welcome') onWelcome(m)
      if (m.type === 'roomSnapshot') {
        snapshotInFlight = false
        setPhase('live')
      }
    }

    // 应用回调挂在每帧自己的 effects 上：`applyServerFrame` 只对真正应用的消息调 onMessage
    const frameEffects: ApplyEffects =
      appliedSubs.size === 0
        ? effects
        : {
            ...effects,
            onMessage: (m) => {
              for (const cb of appliedSubs) cb(m, frame)
            },
          }
    sink(frame, frameEffects)
    for (const m of frame.msgs) {
      for (const cb of messageSubs) cb(m, frame)
    }

    // ★ 回执放在应用之后：ackSeq 的语义是「我已消费到这里」，
    //   抢在前面 ack 会让服务端裁掉重发缓冲里我们其实还没应用的帧
    for (const m of pongs) {
      send({ type: 'pong', nonce: m.nonce, t0: m.t0, t3, ackSeq: ackSeq() })
    }
    // 时钟跳变 > 5s：这段时间的倒计时全是错的，拿一份快照重新对齐
    if (snapped) requestSnapshotOnce('desync')
  }

  function onWelcome(m: Extract<ServerMessage, { type: 'welcome' }>): void {
    clearTimer(helloTimer)
    helloTimer = null
    authRetries = 0
    // ★ 五个重置时机之一（§7.10）
    backoff.reset('welcome')
    // gap:true 表示重发缓冲补不齐，等一份完整快照再说
    setPhase(m.gap ? 'syncing' : 'live')
    heartbeat.start()
    resendOutbox(m.lastProcessedCid)
    const r = resolveConnect
    resolveConnect = null
    r?.()
  }

  function trimOutbox(ack: number): void {
    for (const key of [...outbox.keys()]) {
      if (key <= ack) outbox.delete(key)
    }
  }

  /**
   * 重连后补发。只发 `cid > lastProcessedCid` 的：
   * ≤ 它的服务端已经**提交**过了，重发只会产生幽灵动作（`actionToken` 一次性，
   * 重复的那条也必然被拒，但会白白吃掉一次限流额度）。
   */
  function resendOutbox(lastProcessedCid: number): void {
    trimOutbox(lastProcessedCid)
    for (const frame of [...outbox.values()].sort((a, b) => a.cid - b.cid)) {
      writeFrame(frame)
    }
  }

  // ——————————————————————————————————————————————
  // 建连 / 重连
  // ——————————————————————————————————————————————

  /** ★ 解绑全部处理器再关：否则旧 socket 延迟触发的 onclose 会打掉新连接 */
  function detachSocket(s: SocketLike | null): void {
    if (s === null) return
    s.onopen = null
    s.onclose = null
    s.onerror = null
    s.onmessage = null
  }

  function dropSocket(code: number = WS_CLOSE.CLIENT_DEAD): void {
    const s = socket
    socket = null
    detachSocket(s)
    try {
      s?.close(code)
    } catch {
      // 已经关了就算了
    }
  }

  function scheduleRetry(delayMs: number): void {
    clearTimer(retryTimer)
    if (suspended || closedByUs) return
    setPhase(delayMs > 0 ? 'backoff' : 'connecting')
    retryTimer = timers.setTimeout(() => {
      retryTimer = null
      void open()
    }, delayMs)
  }

  async function open(): Promise<void> {
    if (suspended || closedByUs) return
    clearTimer(retryTimer)
    retryTimer = null
    setPhase('connecting')

    let token: string
    let endpoint: string
    try {
      ;[token, endpoint] = await Promise.all([tokenProvider(), resolveUrl()])
    } catch {
      // 拿不到令牌多半是网络问题，按退避重试（凭据作废由 auth 层自己兜底）
      scheduleRetry(backoff.fail(timers.now()))
      return
    }
    if (suspended || closedByUs) return

    let s: SocketLike
    try {
      s = socketFactory(endpoint)
    } catch {
      scheduleRetry(backoff.fail(timers.now()))
      return
    }
    socket = s

    s.onopen = () => {
      if (socket !== s) return
      setPhase('authenticating')
      const resume = resumeContext()
      const hello: ClientMessage = {
        type: 'hello',
        protocolVersion: PROTOCOL_VERSION,
        accessToken: token,
        deviceId,
        clientVersion,
        ...(resume === null
          ? {}
          : {
              resume: {
                sessionId: resume.sessionId,
                resumeToken: resume.resumeToken,
                lastServerSeq: resume.lastServerSeq,
                roomId: resume.roomId,
                lastSv: resume.lastSv,
                lastHandNo: resume.lastHandNo,
                // ★ 恒为 autoReconnect（§7.6）。这个字段只回答一个问题：「这次**重连**该不该
                //   抢走别人的行动权」——答案永远是不该。
                //   「用户主动打开了这一页」是另一件事，由 room 层的 useActorClaim 发
                //   `claimActor` 表达。别再想着在这里区分「首连 / 重连」来表达用户意图：
                //   新标签页的 hello **根本不带 resume**（store 是空的，没有 session 也没有 view），
                //   这一整个分支都不会执行，改它打不到那个现象；而它真正生效的场合
                //   恰好是「曾被顶掉的设备原地重连」，那里必须是 autoReconnect。
                intent: 'autoReconnect',
              } satisfies ResumeRequest,
            }),
      }
      send(hello)
      clearTimer(helloTimer)
      helloTimer = timers.setTimeout(() => {
        helloTimer = null
        // hello 石沉大海：连上了但服务端没回 welcome，当作死连接
        hardReconnect('dead')
      }, WS.HELLO_TIMEOUT_MS)
    }

    s.onmessage = (ev) => {
      if (socket !== s) return
      handleFrame(ev.data)
    }

    s.onerror = () => {
      // onerror 后必定还会有 onclose，这里什么都不做，避免双重重连
    }

    s.onclose = (ev) => {
      if (socket !== s) return
      socket = null
      detachSocket(s)
      heartbeat.stop()
      clearTimer(helloTimer)
      helloTimer = null
      onClosed(ev.code)
    }
  }

  function onClosed(code: number): void {
    if (closedByUs || suspended) {
      setPhase(suspended ? 'degraded' : 'idle')
      return
    }
    switch (closeBehaviorOf(code)) {
      case 'reconnectNow':
        scheduleRetry(0)
        return
      case 'refreshAuth': {
        authRetries += 1
        if (authRetries > 2) {
          // 刷了还是 4401：本地凭据确实没救了，交给上层回登录页
          setPhase('fatal')
          return
        }
        const refresh = options.refreshToken ?? tokenProvider
        void refresh().then(
          () => {
            scheduleRetry(0)
          },
          () => {
            scheduleRetry(backoff.fail(timers.now()))
          },
        )
        return
      }
      case 'promptReload':
        // 版本不兼容：自动重连没有意义，必须让用户刷新页面
        setPhase('fatal')
        return
      case 'stop':
        // ★ 被新连接顶掉时绝不重连：两台设备会无限对踢
        setPhase(code === WS_CLOSE.SESSION_SUPERSEDED ? 'superseded' : 'idle')
        return
      default:
        scheduleRetry(backoff.fail(timers.now()))
    }
  }

  /** ①解绑 ②关旧的 ③（tokenProvider 内部会判断要不要刷）④建新的。★ 全程不清空牌桌状态 */
  function hardReconnect(reason: ReconnectReason): void {
    if (closedByUs) return
    void reason
    heartbeat.stop()
    clearTimer(helloTimer)
    helloTimer = null
    dropSocket(WS_CLOSE.CLIENT_DEAD)
    suspended = false
    scheduleRetry(0)
  }

  // ——————————————————————————————————————————————
  // 生命周期
  // ——————————————————————————————————————————————

  function attach(): void {
    if (options.lifecycleEnv === null) return
    detachLifecycle?.()
    detachLifecycle = attachLifecycle(
      {
        markBackgrounded: (at) => {
          backgroundedAt = at
        },
        backgroundedAt: () => backgroundedAt,
        resetBackoff: (r: BackoffResetReason) => {
          backoff.reset(r)
        },
        hardReconnect,
        probe: (opts) => {
          heartbeat.probe(opts)
        },
        goOffline: () => {
          suspended = true
          clearTimer(retryTimer)
          retryTimer = null
          heartbeat.stop()
          dropSocket(WS_CLOSE.CLIENT_DEAD)
          setPhase('degraded')
        },
        prepareUnload: () => {
          closedByUs = true
          heartbeat.stop()
          // 主动 close(1001)：服务端立刻开始断线宽限期，而不是等 45s 心跳超时
          dropSocket(WS_CLOSE.PAGE_UNLOAD)
        },
      },
      options.lifecycleEnv === undefined ? {} : { env: options.lifecycleEnv },
    )
  }

  return {
    mode: 'online',

    connect() {
      closedByUs = false
      suspended = false
      if (connectPromise !== null) return connectPromise
      connectPromise = new Promise<void>((resolve) => {
        resolveConnect = resolve
      })
      attach()
      void open()
      return connectPromise
    },

    send,

    onMessage(cb) {
      messageSubs.add(cb)
      return () => {
        messageSubs.delete(cb)
      }
    },

    onApplied(cb) {
      appliedSubs.add(cb)
      return () => {
        appliedSubs.delete(cb)
      }
    },

    onPhase(cb) {
      phaseSubs.add(cb)
      return () => {
        phaseSubs.delete(cb)
      }
    },

    close() {
      closedByUs = true
      clearTimer(retryTimer)
      retryTimer = null
      clearTimer(helloTimer)
      helloTimer = null
      heartbeat.stop()
      detachLifecycle?.()
      detachLifecycle = null
      // 告别一声，服务端立刻回收（比等心跳超时快 45 秒）
      if (socket !== null && socket.readyState === OPEN) send({ type: 'bye' })
      dropSocket(WS_CLOSE.PAGE_UNLOAD)
      outbox.clear()
      connectPromise = null
      resolveConnect = null
      setPhase('idle')
    },

    requestSnapshotOnce,

    retryNow() {
      // 用户点「立即重试」—— 五个退避重置时机之一
      backoff.reset('manualRetry')
      suspended = false
      hardReconnect('manual')
    },

    phase: () => phase,
    clock,
    backoff,
    pendingCount: () => outbox.size,
  }
}

/** `GET /api/config`：WS 地址由服务端下发，客户端不硬编码（§7.12）。 */
export interface ServerConfig {
  readonly wsUrl: string
  readonly protocolVersion: number
  readonly serverEpoch: string
}

export async function fetchServerConfig(baseUrl: string, fetchImpl: typeof fetch = fetch): Promise<ServerConfig> {
  const res = await fetchImpl(`${baseUrl.replace(/\/+$/, '')}/api/config`)
  if (!res.ok) throw new Error(`GET /api/config 失败：${String(res.status)}`)
  return (await res.json()) as ServerConfig
}

/** 客户端生成的一次性动作 id，用于精确回滚那一次乐观 UI。 */
export function newClientActionId(): string {
  return ulid(Date.now())
}
